import {
  PB_ANNOUNCE,
  PB_CHAINING,
  PB_EDIT_PENDING,
  PB_ERROR_CLEAR,
  PB_PLAN_CLEARED,
  PB_PLAN_FAILURE,
  PB_PLAN_REQUEST,
  PB_PLAN_SHOWN,
  PB_PLAN_SUCCESS,
  PB_PLANS_FAILURE,
  PB_PLANS_REQUEST,
  PB_PLANS_SUCCESS,
  PB_PREVIEW_CHECKING,
  PB_REQUIREMENTS_FAILURE,
  PB_REQUIREMENTS_REQUEST,
  PB_REQUIREMENTS_SUCCESS,
  PB_RESET,
  PB_VISIT_START,
  PB_VISIT_END,
  PB_SAVE_FAILURE,
  PB_SAVE_START,
  PB_SAVE_SUCCESS,
  PB_SETTINGS_SUCCESS,
  PB_UNDO_RECORD,
  PB_UNDO_REMAINING,
  PB_UNDO_STEP_BACK,
  PB_UNDO_STEP_FORWARD,
  PB_UNDO_TRACK,
} from './types';
import { emptyHistory, undoHistory } from './undoHistory';

// Errors are kept as plain `{ status, code, message, params }` copies (serializePlannerError); plannerErrorText reads them.
export const initialState = {
  studentUid: null,
  generation: 0,
  visitOwners: {},
  plans: [],
  plansLoading: false,
  plansError: null,
  /** The list request the page made last (loads.js); an answer to any other is stale and dropped. */
  plansRequestId: null,
  /** The plan the page asked for last; an answer for any other is stale and dropped. */
  requestedPlanId: null,
  /** The PlanDetail on the board. Every change Elements accepts returns the full plan, which replaces this. */
  plan: null,
  planLoading: false,
  planError: null,
  /** ProgramProgress by planId, as the plan's last check audited it; dropped when the plan changes. */
  requirementsByPlan: {},
  requirementsLoading: false,
  requirementsError: null,
  settings: null,
  /** Writes in flight. Shown as an inline indicator, never a page loader, which would blank the board. */
  saving: 0,
  /** The last refusal of a plan call, for the page to say. */
  lastError: null,
  /** A consequences check is running before an edit. */
  checking: false,
  /** The edit waiting on the reader: { request, preview, summary, commit, confirmLabel, planId }. */
  pendingEdit: null,
  /** A fix made of several edits is between its steps. */
  chaining: false,
  /** Where a moved course landed, once Elements agreed: { message, validatedOn, id }. */
  announcement: null,
  undo: emptyHistory,
};

const isPlanDetail = (value) => Boolean(value) && typeof value === 'object' && Array.isArray(value.terms) && value.planId != null;

const withoutKey = (map, key) => {
  if (!(key in map)) return map;
  const rest = { ...map };
  delete rest[key];
  return rest;
};

/** A different plan on the board starts with no history and no edit waiting. */
const showPlan = (state, plan) =>
  state.plan && plan && state.plan.planId === plan.planId
    ? { ...state, plan }
    : { ...state, generation: state.generation + 1, plan, undo: emptyHistory, pendingEdit: null, announcement: null };

/** Whether a list answer is for the student on the page and the list request it made last. */
const isCurrentList = (state, action) => action.studentUid === state.studentUid && action.plansRequestId === state.plansRequestId;

function plansReducer(state, action) {
  switch (action.type) {
    case PB_PLANS_REQUEST:
      if (action.studentUid !== state.studentUid) {
        return { ...initialState, generation: state.generation + 1, settings: state.settings, studentUid: action.studentUid, plansRequestId: action.plansRequestId ?? null, plansLoading: true };
      }
      return { ...state, plansRequestId: action.plansRequestId ?? null, plansLoading: true, plansError: null };
    case PB_PLANS_SUCCESS:
      if (!isCurrentList(state, action)) return state;
      // A quiet refresh leaves the loading state to the request that set it.
      if (action.quiet) return { ...state, plans: action.payload || [] };
      return { ...state, plans: action.payload || [], plansLoading: false, plansError: null };
    case PB_PLANS_FAILURE:
      if (!isCurrentList(state, action)) return state;
      return { ...state, plansLoading: false, plansError: action.payload };
    default:
      return undefined;
  }
}

function planReducer(state, action) {
  switch (action.type) {
    case PB_PLAN_REQUEST: {
      const switching = !state.plan || state.plan.planId !== action.planId;
      const next = { ...state, requestedPlanId: action.planId, planLoading: true, planError: null };
      return switching ? { ...next, generation: state.generation + 1, saving: 0, checking: false, chaining: false, plan: null, undo: emptyHistory, pendingEdit: null, announcement: null } : next;
    }
    case PB_PLAN_SUCCESS: {
      const plan = action.payload;
      if (!isPlanDetail(plan)) return { ...state, planLoading: false };
      if (!action.select && plan.planId !== state.requestedPlanId) return state;
      return { ...showPlan(state, plan), requestedPlanId: plan.planId, planLoading: false, planError: null };
    }
    case PB_PLAN_FAILURE:
      if (action.planId !== state.requestedPlanId) return state;
      return { ...state, planLoading: false, planError: action.payload };
    case PB_PLAN_CLEARED:
      return { ...state, generation: state.generation + 1, saving: 0, checking: false, chaining: false, plan: null, requestedPlanId: null, planLoading: false, planError: null, undo: emptyHistory, pendingEdit: null, announcement: null };
    case PB_PLAN_SHOWN:
      return isPlanDetail(action.payload) && state.plan && state.plan.planId === action.payload.planId ? { ...state, plan: action.payload } : state;
    default:
      return undefined;
  }
}

function requirementsReducer(state, action) {
  switch (action.type) {
    case PB_REQUIREMENTS_REQUEST:
      return { ...state, requirementsLoading: true, requirementsError: null };
    case PB_REQUIREMENTS_SUCCESS:
      return { ...state, requirementsLoading: false, requirementsByPlan: { ...state.requirementsByPlan, [action.planId]: action.payload } };
    case PB_REQUIREMENTS_FAILURE:
      return { ...state, requirementsLoading: false, requirementsError: action.payload };
    case PB_SETTINGS_SUCCESS:
      return { ...state, settings: action.payload };
    default:
      return undefined;
  }
}

function saveReducer(state, action) {
  switch (action.type) {
    case PB_SAVE_START:
      return { ...state, saving: state.saving + 1 };
    case PB_SAVE_SUCCESS: {
      const saving = Math.max(0, state.saving - 1);
      const plan = action.payload;
      if (!isPlanDetail(plan) || !state.plan || state.plan.planId !== plan.planId) return { ...state, saving };
      // A reorder changes nothing the audit sees, so the requirements it read stay good.
      const requirementsByPlan = action.keepRequirements ? state.requirementsByPlan : withoutKey(state.requirementsByPlan, plan.planId);
      return { ...state, saving, plan, requirementsByPlan };
    }
    case PB_SAVE_FAILURE:
      return { ...state, saving: Math.max(0, state.saving - 1), lastError: action.payload };
    case PB_ERROR_CLEAR:
      return { ...state, lastError: null };
    default:
      return undefined;
  }
}

function editReducer(state, action) {
  switch (action.type) {
    case PB_PREVIEW_CHECKING:
      return { ...state, checking: Boolean(action.payload) };
    case PB_EDIT_PENDING:
      return { ...state, pendingEdit: action.payload || null };
    case PB_CHAINING:
      return { ...state, chaining: Boolean(action.payload) };
    case PB_ANNOUNCE:
      return { ...state, announcement: { ...action.payload, id: (state.announcement ? state.announcement.id : 0) + 1 } };
    case PB_UNDO_RECORD:
    case PB_UNDO_TRACK:
    case PB_UNDO_REMAINING:
    case PB_UNDO_STEP_BACK:
    case PB_UNDO_STEP_FORWARD:
      return { ...state, undo: undoHistory(state.undo, action) };
    default:
      return undefined;
  }
}

const SECTIONS = [plansReducer, planReducer, requirementsReducer, saveReducer, editReducer];

const planBuilderReducer = (state = initialState, action) => {
  if (action.type === PB_VISIT_START) return { ...state, visitOwners: { ...state.visitOwners, [action.scope]: action.owner } };
  if (action.type === PB_VISIT_END) {
    if (state.visitOwners[action.scope] !== action.owner) return state;
    if (action.scope === 'tab') return { ...initialState, generation: state.generation + 1 };
    const next = planReducer(state, { type: PB_PLAN_CLEARED });
    const visitOwners = { ...next.visitOwners };
    delete visitOwners[action.scope];
    return { ...next, visitOwners };
  }
  if (action.type === PB_RESET) return { ...initialState, generation: state.generation + 1 };
  if (action.workspaceGeneration !== undefined && action.workspaceGeneration !== state.generation) return state;
  for (const section of SECTIONS) {
    const next = section(state, action);
    if (next !== undefined) return next;
  }
  return state;
};

export default planBuilderReducer;
